import React from "react";
import { useQuery } from "@tanstack/react-query";
import { billingTransactionKeys } from "@/hooks/useBillingTransactions";
import { billingTransactionService } from "@/services/billing-transactions-service";
import {
  computeBillingBalanceStats,
  type BillingLikeTransaction,
} from "@/utils/billingBalance";
import { AmountCell } from "@/components/pages/settlement/components/table-cells/AmountCell";

const BILLING_PAGE_LIMIT = 100;

export function ReservationBalanceCell({
  reservationId,
  embeddedTransactions,
}: {
  reservationId: string;
  embeddedTransactions: BillingLikeTransaction[];
}) {
  const hasEmbedded = embeddedTransactions.length > 0;

  const { data, isLoading, isError } = useQuery({
    queryKey: billingTransactionKeys.list({ bookingId: reservationId }),
    queryFn: () =>
      billingTransactionService.getBillingTransactions(
        { bookingId: reservationId },
        1,
        BILLING_PAGE_LIMIT,
      ),
    enabled: !hasEmbedded && reservationId.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const transactions = hasEmbedded
    ? embeddedTransactions
    : (data?.transactions ?? []);

  const stats = computeBillingBalanceStats(transactions);

  if (!hasEmbedded && isError) {
    return <div className="text-center text-[10px] text-neutral-400">—</div>;
  }

  if (!hasEmbedded && isLoading) {
    return (
      <div className="flex justify-center">
        <span className="inline-block h-4 w-8 animate-pulse rounded bg-neutral-200" />
      </div>
    );
  }

  return <AmountCell amount={stats.balanceMinor} currency={stats.currency} />;
}
